import { useState, useEffect } from 'react'
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from './useAuth'
import type { Collection } from '@/types/models'

interface UseCollectionsProps {
  userId?: string // Optional userId to fetch collections for a specific user
}

export function useCollections({ userId }: UseCollectionsProps = {}) {
  const { user } = useAuthState()
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Determine which userId to use (provided userId or current user's id)
    const targetUserId = userId || user?.uid
    
    if (!targetUserId) {
      setCollections([])
      setLoading(false)
      return
    }
    
    setLoading(true)
    
    // Query collections for the specified user with real-time listener
    const collectionsRef = collection(db, 'collections')
    const collectionsQuery = query(
      collectionsRef,
      where('userId', '==', targetUserId),
      orderBy('createdAt', 'desc')
    )
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      collectionsQuery,
      (querySnapshot) => {
        const collectionsData: Collection[] = []
        
        querySnapshot.forEach((doc) => {
          const data = doc.data() as Omit<Collection, 'id'>
          collectionsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt as any)?.toDate?.() || new Date()
          })
        })
        
        setCollections(collectionsData)
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('[useCollections] Error listening to collections:', err)
        setError(err instanceof Error ? err : new Error(String(err)))
        setLoading(false)
      }
    )
    
    // Cleanup listener on unmount
    return () => unsubscribe()
  }, [user?.uid, userId])

  // Function to fetch a single collection by ID
  const getCollection = async (collectionId: string): Promise<Collection | null> => {
    try {
      const collectionRef = doc(db, 'collections', collectionId)
      const collectionDoc = await getDoc(collectionRef)
      
      if (collectionDoc.exists()) {
        const data = collectionDoc.data() as Omit<Collection, 'id'>
        return {
          id: collectionDoc.id,
          ...data,
          createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt as any)?.toDate?.() || new Date()
        }
      }
      
      return null
    } catch (err) {
      console.error('[useCollections] Error fetching collection:', err)
      throw err
    }
  }

  return {
    collections,
    loading,
    error,
    getCollection
  }
}
